import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
} from 'src/shared/constants/table-settings-constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { Dispatch, useCallback, useMemo } from 'react'
import { Chip, IconButton } from '@mui/material'
import { DeleteIcon, EditIcon } from 'src/shared/icons/Icons'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { EvaluationCriteriaGridToolbar } from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/EvaluationCriteriaGridToolbar'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import {
  DataGridDisabledStyled,
  DataGridInactiveRowCssClassId,
} from 'src/shared/styled/data-grid/DataGridDisabledStyled'
import { S } from 'src/shared/styled/S'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import {
  getTranslationForProcess,
  getTranslationForStatus,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'

interface EvaluationCriteriaGridProps {
  dataSettings: ConfigurationGridDataSettings
  tableSettings: ConfigurationTablePageSortSettings
  setTableSettings: Dispatch<ConfigurationTablePageSortSettings>
  setCriteriaToBeDeleted: Dispatch<number>
}

export const EvaluationCriteriaGrid = ({
  dataSettings,
  tableSettings,
  setTableSettings,
  setCriteriaToBeDeleted,
}: EvaluationCriteriaGridProps) => {
  const { rows, rowsCount, gridLoading } = dataSettings
  const { paginationModel, sortModel } = tableSettings
  const { getMessage } = useMessageSource()
  const gridTranslations = useGridTranslateHook()
  const navigate = useDelayedNavigate()

  const columns = useMemo(() => {
    return [
      {
        field: 'criteria_id',
        headerName: getMessage('label.configuration.evaluation.criteria.grid.id'),
        flex: 0.5,
        sortable: true,
        filterable: false,
      },
      {
        field: 'description',
        headerName: getMessage('label.description'),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          return (
            <S.Box.TruncateBox>
              <HtmlRenderer html={row.description} truncate variant="body2" />
            </S.Box.TruncateBox>
          )
        },
      },
      {
        field: 'criteria_type',
        headerName: getMessage('label.criteria_type'),
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          if (params.value) {
            return getMessage(`label.${params.value.toLowerCase()}.criteria`)
          }
          return ''
        },
      },
      {
        field: 'allocation',
        headerName: getMessage('label.configuration.grid.allocation'),
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          if (params.value) {
            return <Chip label={getTranslationForProcess(getMessage, params.value)} />
          }
          return ''
        },
      },
      {
        field: 'status',
        headerName: getMessage('label.status'),
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const statusKey = params.value ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE
          return <Chip label={getTranslationForStatus(getMessage, statusKey)} />
        },
      },
      {
        field: 'actions',
        headerName: getMessage('label.configuration.grid.actions'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.5,
        resizable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const onEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()

            const criteriaId = params.row.id
            const route = ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationEvaluationCriteriaEdit.params({
              criteriaId,
            })
            navigate(route)
          }
          const onDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()

            const criteriaId = params.row.id
            setCriteriaToBeDeleted(criteriaId)
          }

          return (
            <>
              {params.row.is_deletable && (
                <span>
                  <IconButton onClick={onDeleteClick} color="primary">
                    <DeleteIcon />
                  </IconButton>
                </span>
              )}
              <span>
                <IconButton onClick={onEditClick} color="primary">
                  <EditIcon />
                </IconButton>
              </span>
            </>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, navigate, setCriteriaToBeDeleted])

  const handleCreateCriteriaClick = useCallback(() => {
    navigate(ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationEvaluationCriteriaCreate.params({}))
  }, [navigate])

  return (
    <DataGridContainer>
      <DataGridPro
        rows={rows}
        rowCount={rowsCount}
        columns={columns}
        getRowClassName={(params) => (params.row.status ? '' : DataGridInactiveRowCssClassId)}
        loading={gridLoading}
        localeText={gridTranslations}
        disableColumnPinning
        disableRowSelectionOnClick
        disableColumnMenu
        pagination
        paginationModel={paginationModel}
        sortModel={sortModel}
        pageSizeOptions={[10, 25, 50]}
        filterMode="server"
        paginationMode="server"
        sortingMode="server"
        onPaginationModelChange={(newPagination) => {
          setTableSettings({
            ...tableSettings,
            paginationModel: newPagination,
          })
        }}
        onSortModelChange={(newSort) => {
          setTableSettings({
            ...tableSettings,
            sortModel: newSort,
          })
        }}
        sx={{
          boxShadow: 'none',
          border: 'none',
          ...DataGridDisabledStyled,
        }}
        slots={{
          toolbar: () => (
            <EvaluationCriteriaGridToolbar numResults={rowsCount} onAddCriteriaClick={handleCreateCriteriaClick} />
          ),
        }}
      />
    </DataGridContainer>
  )
}
